import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import CustomCard from "components/CustomCard";
import SignInForm from "components/SignInForm";
import Swipper from "components/Swipper";
import useAuthentication from "hooks/useAuthentication";

const WatchlistsPageTabs = dynamic(
  () => import("../WatchlistsPageTabs/WatchlistPageTabsMobile")
);
const WatchlistsPageWidget = dynamic(() => import("../WatchlistsPageWidget"));
const WatchlistUpdates = dynamic(() => import("../WatchlistUpdates"));

type WatchlistsPageMobileProps = { showCTAs?: boolean };

const WatchlistsPageMobile = ({ showCTAs }: WatchlistsPageMobileProps) => {
  const router = useRouter();
  const watchlistId = router.query.watchlist as string;
  const currentTab = (router.query.tab as string) || "prices";

  const { authenticated } = useAuthentication();

  const [index, setIndex] = useState(0);
  const [showAddEntity, setShowAddEntity] = useState(false);

  const content = useMemo(() => {
    switch (currentTab) {
      case "prices":
        return (
          <WatchlistsPageWidget
            showAddEntity={showAddEntity}
            setShowAddEntity={setShowAddEntity}
          />
        );
      case "updates":
        return (
          <WatchlistUpdates
            showAddEntity={showAddEntity}
            setShowAddEntity={setShowAddEntity}
          />
        );
      default:
        return (
          <WatchlistsPageWidget
            showAddEntity={showAddEntity}
            setShowAddEntity={setShowAddEntity}
          />
        );
    }
  }, [currentTab, showAddEntity]);

  const tabs = useMemo(
    () => [
      {
        type: "prices",
        name: "PRICES",
        href: `/watchlists?watchlist=${watchlistId}&tab=prices`,
      },
      {
        type: "updates",
        name: "UPDATES",
        href: `/watchlists?watchlist=${watchlistId}&tab=updates`,
      },
    ],
    [watchlistId]
  );

  useEffect(() => {
    setShowAddEntity(false);

    if (!currentTab && watchlistId) router.push(tabs[0].href);

    if (currentTab) {
      setIndex(currentTab === "prices" ? 0 : 1);
    }
  }, [currentTab, router, tabs, watchlistId]);

  if (!authenticated) {
    return (
      <div
        className={twMerge(
          "bg-[#eee]",
          "mt-[54px] py-[10px]",
          "flex flex-col min-h-screen p-[15px]"
        )}
      >
        <CustomCard>
          <SignInForm />
        </CustomCard>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "bg-white",
        "mt-[44px] py-[10px]",
        "flex flex-col min-h-screen"
      )}
    >
      <WatchlistsPageTabs
        tabs={tabs}
        currentTab={currentTab}
        showAddEntity={showAddEntity}
        onAddWatchlist={() => {
          setShowAddEntity(!showAddEntity);
        }}
      />
      <CustomCard
        className={twMerge("p-0 rounded-none", showCTAs ? "" : "pt-[7.5px]")}
      >
        <Swipper
          index={index}
          maxIndex={tabs.length}
          onIndexChange={(index) => {
            router.push(tabs[index].href);
          }}
        >
          <div className={classNames("bg-white")}>{content}</div>
        </Swipper>
      </CustomCard>
    </div>
  );
};

export default WatchlistsPageMobile;
